import React, { memo, useEffect, useState } from 'react';
import { Button, ButtonSize, ButtonVariant, DatePicker } from '@cmhc-schl/hearth-ui';
import { Box, FormLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import moment from 'moment';
import i18next from 'i18next';

import { OfferDateConfig } from './types';

interface Props {
  offerDateConfig?: OfferDateConfig | undefined;
  onDateChange?: (date: string | null) => void;
}

const OfferDateComponent: React.FC<Props> = ({ offerDateConfig, onDateChange }) => {
  const {
    headerText,
    labelText,
    btnCancelText = 'Cancel',
    btnSubmitText = 'Submit',
    cancel,
    submit,
    date: currentDate,
    language = 'en'
  } = offerDateConfig ?? {};

  const { t } = useTranslation('HbtMasterPoolDetails');

  const [date, setDate] = useState<Date | null>(moment().startOf('day').toDate());
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const minDate = moment().subtract(15, 'years').startOf('day').toDate();
  const maxDate = moment().startOf('day').toDate();
  useEffect(() => {
    handleDateChange(date);
  }, []);

  const handleDateChange = (newDate: Date | null | undefined) => {
    const min = moment(minDate);
    const max = moment(maxDate);

    if (!newDate || isNaN(newDate.getTime())) {
      setError(true);
      setErrorMessage(t('Enter-Valid-Date') || 'Enter a valid date.');
      setDate(null);
      if (onDateChange) onDateChange(null);
      return;
    }

    const selectedDate = moment(newDate).startOf('day');

    const isItSameDay = moment(currentDate).startOf('day').isSame(selectedDate, 'day');

    if (selectedDate.isBefore(min) || isItSameDay) {
      setError(true);
      selectedDate.isBefore(min)
        ? setErrorMessage(t('Enter-Valid-Date'))
        : setErrorMessage(t('Same-Date-Error'));
      setDate(newDate);
      if (onDateChange) onDateChange(null);
      return;
    }

    if (selectedDate.isAfter(max)) {
      setError(true);
      setErrorMessage(t('Enter-Valid-Date'));
      setDate(newDate);
      if (onDateChange) onDateChange(null);
      return;
    }

    setError(false);
    setErrorMessage('');
    setDate(newDate);
    const formatted = selectedDate.format('YYYY-MM-DD');
    if (onDateChange) onDateChange(formatted);
  };

  return (
    <Box className={styles.offerDateContainer}>
      <FormLabel className={styles.offerDateHeader} htmlFor="offer-date">
        {headerText}
      </FormLabel>

      <div className={styles.datePickerContainer}>
        <div
          className={`${styles.leftColumn} ${i18next.language === 'fr' ? styles.leftColumnFr : ''}`}
        >
          <DatePicker
            id="offer-date"
            format="yyyy-MM-dd"
            value={date}
            onChange={handleDateChange}
            label={labelText}
            className={styles.datePickerBackGround}
            error={error}
            errorMessage={error ? errorMessage : ''}
            placeholderText="YYYY-MM-DD"
            required={true}
            localLang={language}
            maxDate={maxDate}
            minDate={minDate}
          />
        </div>

        <div className={styles.rightColumn}>
          <div className={styles.buttonsWrapper}>
            <Button
              type="button"
              onClick={cancel}
              name="actions"
              size={ButtonSize.SMALL}
              variant={ButtonVariant.TERTIARY}
              text={{ value: btnCancelText }}
              ariaText={{ value: btnCancelText }}
              className={styles.button}
            />
            <Button
              id="btn-submit"
              type="button"
              onClick={submit}
              name="actions"
              size={ButtonSize.SMALL}
              variant={ButtonVariant.SECONDARY}
              text={{ value: btnSubmitText }}
              ariaText={{ value: btnSubmitText }}
              className={styles.button}
            />
          </div>
        </div>
      </div>
    </Box>
  );
};

export const OfferDate = memo(OfferDateComponent) as typeof OfferDateComponent;
